import Phaser from 'phaser';
import { GALLERY, N1_TEXTURE_KEY } from '../config';
import {
  N1_ACTIONS,
  N1_AERIAL,
  frameIndexOf,
  frameName,
  rootLiftPx,
  tierOfLabel,
  type N1ActionId,
  type N1Manifest,
  type N1TierLabel,
} from '../assets/n1Manifest';
import type { RegisteredAction } from '../animation/registerN1Animations';
import { snapshot, publishSnapshot } from '../debug/snapshot';
import { EdgeKeys } from '../input/controls';

interface GalleryData {
  manifest: N1Manifest;
  tierLabel: N1TierLabel;
  registered: RegisteredAction[];
}

interface Entry {
  id: N1ActionId;
  sprite: Phaser.GameObjects.Sprite;
  groundY: number;
}

/**
 * All 13 N1 actions side by side on ground lines, 1:1 pixels, positioned by the pivot.
 * F flips facing (runtime mirror — there are no right-facing frames), 1–5 set the time scale,
 * L previews the authored jump arc by offsetting aerial sprites with rootLift, R resets.
 */
export class GalleryScene extends Phaser.Scene {
  private manifest!: N1Manifest;
  private tierLabel!: N1TierLabel;
  private registered: RegisteredAction[] = [];
  private entries: Entry[] = [];
  private facing: 'left' | 'right' = 'left';
  private rateIndex = 0;
  private liftPreview = false;
  private hud!: Phaser.GameObjects.Text;
  private commands: EdgeKeys<
    'flip' | 'lift' | 'reset' | 'room' | 'r0' | 'r1' | 'r2' | 'r3' | 'r4'
  > | null = null;

  constructor() {
    super('Gallery');
  }

  init(data: GalleryData): void {
    this.manifest = data.manifest;
    this.tierLabel = data.tierLabel;
    this.registered = data.registered;
  }

  create(): void {
    const { width } = this.scale;
    const [pivotX, pivotY] = this.manifest.geometry.pivot;
    const slot = width / GALLERY.columns;
    this.entries = [];

    for (const groundY of GALLERY.groundY) {
      this.add.line(0, 0, 0, groundY, width, groundY, 0xd0d0c8).setOrigin(0, 0).setLineWidth(1);
    }

    N1_ACTIONS.forEach((id, i) => {
      const col = i % GALLERY.columns;
      const row = Math.floor(i / GALLERY.columns);
      const groundY = GALLERY.groundY[row] ?? GALLERY.groundY[0];
      const x = slot * col + slot / 2;
      const sprite = this.add
        .sprite(x, groundY, N1_TEXTURE_KEY, frameName(id, 0))
        .setOrigin(pivotX, pivotY);
      sprite.play(id);
      this.add
        .text(x, groundY + GALLERY.labelOffset, id, {
          color: '#33332f',
          fontSize: '13px',
          fontFamily: 'system-ui, sans-serif',
        })
        .setOrigin(0.5, 0);
      this.entries.push({ id, sprite, groundY });
    });

    this.hud = this.add
      .text(16, 12, '', { color: '#33332f', fontSize: '15px', fontFamily: 'monospace' })
      .setOrigin(0, 0);

    const keyboard = this.input.keyboard;
    if (keyboard) {
      // Tracked Key objects with replay-proof edge counters, polled once per frame in update() —
      // never raw `keydown-X` events (see src/game/input/edgeCounter.ts for the Phaser 3.90 pitfall).
      const K = Phaser.Input.Keyboard.KeyCodes;
      this.commands = new EdgeKeys(keyboard, {
        flip: K.F,
        lift: K.L,
        reset: K.R,
        room: K.G,
        r0: K.ONE,
        r1: K.TWO,
        r2: K.THREE,
        r3: K.FOUR,
        r4: K.FIVE,
      });
    }

    snapshot.phase = 'gallery';
    snapshot.ready = true;
    snapshot.player = null;
    snapshot.room = null;
    snapshot.endless = null;
    snapshot.renderer = this.game.renderer.type === Phaser.WEBGL ? 'webgl' : 'canvas';
    this.refreshSnapshotActions();
    this.refreshHud();
    publishSnapshot();
  }

  override update(): void {
    snapshot.renderedFrames += 1;
    const commands = this.commands?.pressed();
    if (commands?.has('room')) {
      this.toRoom();
      return;
    }
    if (commands?.has('flip')) this.setFacing(this.facing === 'left' ? 'right' : 'left');
    if (commands?.has('lift')) this.setLiftPreview(!this.liftPreview);
    if (commands?.has('reset')) this.reset();
    (['r0', 'r1', 'r2', 'r3', 'r4'] as const).forEach((name, i) => {
      if (commands?.has(name)) this.setRate(i);
    });
    for (const e of this.entries) {
      if (!N1_AERIAL.includes(e.id)) continue;
      const index = frameIndexOf(e.sprite.frame.name);
      const lift = this.liftPreview ? rootLiftPx(this.manifest, e.id, this.tierLabel, index) : 0;
      e.sprite.y = e.groundY - lift;
    }
  }

  private setFacing(facing: 'left' | 'right'): void {
    this.facing = facing;
    for (const e of this.entries) e.sprite.setFlipX(facing === 'right');
    snapshot.facing = facing;
    this.refreshHud();
  }

  private setRate(index: number): void {
    this.rateIndex = index;
    const rate = GALLERY.rates[index] ?? 1;
    this.anims.globalTimeScale = rate;
    snapshot.rate = rate;
    this.refreshHud();
  }

  private setLiftPreview(on: boolean): void {
    this.liftPreview = on;
    snapshot.liftPreview = on;
    this.refreshHud();
  }

  private toRoom(): void {
    this.scene.start('Endless', {
      manifest: this.manifest,
      tierLabel: this.tierLabel,
      registered: this.registered,
    });
  }

  private reset(): void {
    this.setFacing('left');
    this.setRate(0);
    this.setLiftPreview(false);
    for (const e of this.entries) e.sprite.play(e.id);
  }

  private refreshSnapshotActions(): void {
    const texture = this.textures.get(N1_TEXTURE_KEY);
    snapshot.actions = this.entries.map((e) => ({
      id: e.id,
      frames: this.manifest.actions[e.id].tiers[tierOfLabel(this.tierLabel).tier].frames,
      textureHas: texture.has(frameName(e.id, 0)),
      playing: e.sprite.anims.isPlaying,
    }));
  }

  private refreshHud(): void {
    const rate = GALLERY.rates[this.rateIndex] ?? 1;
    this.hud.setText(
      [
        `Georgi N1 gallery — tier ${this.tierLabel} · ${this.manifest.sourceRelease} → ${this.manifest.release}`,
        `facing ${this.facing.toUpperCase()} (F)  ·  rate ${rate}× (1–5, 1× = review timing)  ·  rootLift preview ${this.liftPreview ? 'ON' : 'OFF'} (L)  ·  R reset  ·  G play the game`,
      ].join('\n'),
    );
  }
}
